import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Loader2, 
  AlertCircle, 
  Download, 
  Printer,
  Calendar,
  ExternalLink,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { shareApi } from '../services/api';
import html2pdf from 'html2pdf.js';
import toast from 'react-hot-toast';

export const ShareDocumentPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  
  const [documentData, setDocumentData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // ── Resposta à proposta (Aceitar/Rejeitar) ──
  const [responding, setResponding] = useState(false);
  const [responseDone, setResponseDone] = useState<'accept' | 'reject' | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (token) loadDocument();
  }, [token]);

  const loadDocument = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await shareApi.getSharedProposal(token!);
      if (!data) throw new Error('Documento não encontrado.');
      setDocumentData(data);
    } catch (err: any) {
      console.error('Erro ao carregar documento:', err);
      setError(err.message || 'Esta ligação não é válida.');
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (action: 'accept' | 'reject') => {
    if (!token) return;
    setResponding(true);
    try {
      await shareApi.respondProposal(token, {
        action,
        reason: action === 'reject' ? rejectReason : '',
      });
      setResponseDone(action);
      setDocumentData((prev: any) => ({
        ...prev,
        status: action === 'accept' ? 'accepted' : 'rejected',
      }));
      toast.success(action === 'accept' ? 'Proposta aceite! Obrigado.' : 'Proposta rejeitada. Registámos a sua decisão.');
    } catch (err: any) {
      toast.error(err.message || 'Não foi possível registar a resposta.');
    } finally {
      setResponding(false);
      setShowRejectModal(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!contentRef.current) return;
    const opt = {
      margin: 0,
      filename: `proposta-${token?.slice(-6)}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(contentRef.current).save();
  };

  // Helper para formatar endereço (evita erro de renderização de objeto)
  const formatAddress = (address: any) => {
    if (!address) return '';
    if (typeof address === 'string') return address;
    const { street, city, state, country } = address;
    return [street, city, state, country].filter(Boolean).join(', ');
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
    </div>
  );

  if (error || !documentData) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-3xl shadow-xl max-w-md text-center border border-red-100">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-gray-600 font-medium">{error}</p>
      </div>
    </div>
  );

  // --- LÓGICA DE CÁLCULOS COM IVA ---
  const items = documentData.items || documentData.lineItems || [];
  const taxRate = Number(documentData.taxRate) || Number(documentData.company?.taxRate) || 0; // Ex: 17 para 17%

  const computedSubtotal = items.reduce((acc: number, item: any) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.unitPrice) || Number(item.price) || 0;
    return acc + (qty * price);
  }, 0);

  const subtotal = documentData.subtotal !== undefined ? Number(documentData.subtotal) : computedSubtotal;
  const taxAmount = documentData.taxAmount !== undefined ? Number(documentData.taxAmount) : (subtotal * (taxRate / 100));
  const total = documentData.total !== undefined ? Number(documentData.total) : (subtotal + taxAmount);

  const canRespond = ['sent', 'opened'].includes(documentData?.status);
  const isExpired = documentData?.status === 'expired';

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20">
      {/* Navbar de Ações */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-xs">MPV</div>
            <span className="font-bold text-gray-900 hidden sm:block">Visualização de Proposta</span>
          </div>
          <div className="flex items-center gap-3">
            {canRespond && !responseDone && (
              <>
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={responding}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm border border-red-200 text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                >
                  <XCircle size={18}/> Rejeitar
                </button>
                <button
                  onClick={() => handleRespond('accept')}
                  disabled={responding}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-emerald-100 transition disabled:opacity-50"
                >
                  <CheckCircle2 size={18}/> {responding ? 'A processar...' : 'Aceitar Proposta'}
                </button>
              </>
            )}
            <button onClick={() => window.print()} className="p-2.5 text-gray-500 hover:bg-gray-100 rounded-xl transition">
              <Printer size={20}/>
            </button>
            <button onClick={handleDownloadPDF} className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-indigo-100 transition text-sm">
              <Download size={18}/> Descarregar PDF
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-8">
        {/* Banner de estado (expirada / respondida) */}
        {isExpired && (
          <div className="mb-6 flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-800 px-5 py-4 rounded-2xl">
            <AlertCircle size={20} className="shrink-0" />
            <div>
              <p className="font-bold text-sm">Esta proposta já expirou.</p>
              <p className="text-xs text-amber-700">Contacte o emissor para renovar a oferta.</p>
            </div>
          </div>
        )}
        {responseDone === 'accept' && (
          <div className="mb-6 flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-800 px-5 py-4 rounded-2xl">
            <CheckCircle2 size={20} className="shrink-0" />
            <div>
              <p className="font-bold text-sm">Proposta aceite!</p>
              <p className="text-xs text-emerald-700">A sua resposta foi enviada ao emissor. Obrigado.</p>
            </div>
          </div>
        )}
        {responseDone === 'reject' && (
          <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-800 px-5 py-4 rounded-2xl">
            <XCircle size={20} className="shrink-0" />
            <div>
              <p className="font-bold text-sm">Proposta rejeitada.</p>
              <p className="text-xs text-red-700">A sua decisão foi registada. Obrigado pelo seu tempo.</p>
            </div>
          </div>
        )}
        {!responseDone && ['accepted', 'rejected'].includes(documentData?.status) && (
          <div className="mb-6 flex items-center gap-3 bg-gray-50 border border-gray-200 text-gray-700 px-5 py-4 rounded-2xl">
            <CheckCircle2 size={20} className="shrink-0 text-gray-400" />
            <p className="text-sm font-medium">
              Esta proposta já foi {documentData.status === 'accepted' ? 'aceite' : 'rejeitada'}.
            </p>
          </div>
        )}
        {/* Folha A4 */}
        <div ref={contentRef} className="bg-white shadow-2xl rounded-sm overflow-hidden min-h-[297mm]">
          
          {/* Cabeçalho Profissional */}
          <div className="p-12 ">
            <div className="flex justify-between items-start mb-10">
              <div>
                <h1 className="text-3xl font-black text-gray-900 uppercase tracking-tighter">Proposta Comercial</h1>
                <div className="flex items-center gap-2 mt-1">
                   <Calendar size={14} className="text-gray-400" />
                   <p className="text-gray-400 font-medium text-xs uppercase tracking-widest">
                     Expira em: {new Date(documentData.expiresAt).toLocaleDateString('pt-MZ')}
                   </p>
                </div>
              </div>
              {documentData.company?.logo && (
                <img src={documentData.company.logo} className="h-16 object-contain" alt="Logo" />
              )}
            </div>

            <div className="grid grid-cols-2 gap-10 text-sm">
              <div>
                <p className="font-black text-indigo-600 uppercase text-[10px] mb-2 tracking-widest text-opacity-70">Emitido por:</p>
                <p className="font-bold text-gray-900 text-lg">{documentData.company?.name}</p>
                <p className="text-gray-500 leading-relaxed">{formatAddress(documentData.company?.address)}</p>
                <p className="text-gray-500 font-medium mt-1">{documentData.company?.email}</p>
              </div>
              <div>
                <p className="font-black text-indigo-600 uppercase text-[10px] mb-2 tracking-widest text-opacity-70">Para:</p>
                <p className="font-bold text-gray-900 text-lg">{documentData.client?.name || documentData.recipients?.[0]?.name}</p>
                <p className="text-gray-500">{documentData.client?.email || documentData.recipients?.[0]?.email}</p>
              </div>
            </div>
          </div>

          <div className="p-12">
            {/* Tabela de Itens */}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-900 text-left">
                  <th className="py-4 font-black uppercase text-[11px] tracking-widest">Descrição do Item/Serviço</th>
                  <th className="py-4 font-black uppercase text-[11px] tracking-widest text-center w-20">Qtd</th>
                  <th className="py-4 font-black uppercase text-[11px] tracking-widest text-right w-32">Unitário</th>
                  <th className="py-4 font-black uppercase text-[11px] tracking-widest text-right w-32">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item: any, idx: number) => {
                  const qty = Number(item.quantity) || 1;
                  const price = Number(item.unitPrice || item.price) || 0;
                  return (
                    <tr key={idx} className="group">
                      <td className="py-5">
                        <span className="block font-bold text-gray-900">{item.description || item.name}</span>
                      </td>
                      <td className="py-5 text-center text-gray-600 font-medium">{qty}</td>
                      <td className="py-5 text-right text-gray-600">{price.toLocaleString('pt-MZ')}</td>
                      <td className="py-5 text-right font-bold text-gray-900">{(qty * price).toLocaleString('pt-MZ')} MT</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Resumo Financeiro com IVA */}
            <div className="mt-10 flex justify-end">
              <div className="w-72 space-y-3">
                <div className="flex justify-between text-gray-500 font-medium">
                  <span>Subtotal</span>
                  <span>{subtotal.toLocaleString('pt-MZ')} MT</span>
                </div>
                
                {taxRate > 0 && (
                  <div className="flex justify-between text-gray-500 font-medium">
                    <span>IVA ({taxRate}%)</span>
                    <span>{taxAmount.toLocaleString('pt-MZ')} MT</span>
                  </div>
                )}

                <div className="flex justify-between text-2xl font-black border-t-4 border-gray-900 pt-4 mt-2">
                  <span>TOTAL</span>
                  <span className="text-indigo-600">{total.toLocaleString('pt-MZ')} MT</span>
                </div>
              </div>
            </div>

            {/* Mensagem e Notas */}
            {documentData.message && (
              <div className="mt-20">
                <h4 className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] mb-4">Notas e Condições</h4>
                <div className="p-6 bg-gray-50 rounded-2xl border-l-4 border-indigo-600">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap italic text-sm">
                    {documentData.message}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Externo */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 text-[10px] uppercase tracking-widest flex items-center justify-center gap-2">
            <ExternalLink size={12} />
            Documento Seguro • Gerado por Meu Ponto de Venda
          </p>
        </div>
      </div>

      {/* Modal: motivo da rejeição */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 shadow-2xl">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Rejeitar proposta</h2>
            <p className="text-sm text-gray-500 mb-5">
              Gostaríamos de perceber o motivo. A sua resposta será enviada ao emissor (opcional).
            </p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={4}
              placeholder="Ex: preço acima do orçamento, mudámos de fornecedor..."
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all resize-none"
            />
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={responding}
                className="flex-1 px-6 py-3 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleRespond('reject')}
                disabled={responding}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-xl font-bold shadow-lg shadow-red-100 hover:bg-red-700 active:scale-95 transition-all disabled:opacity-60"
              >
                {responding ? 'A processar...' : 'Confirmar Rejeição'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};